import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import { PhoneLink } from 'components/PhoneLink'
import { usePermissions } from 'hooks/usePermissions'
import { useSelector } from 'react-redux'
import { partiesMapWithAccountTypes, pullThroughRatioPartiesMap } from 'reducers/loanDetail.reducer'
import { Tooltip } from 'stories/components'
import { getFullName, getPrice2decimal, phoneConvertor, toCamelCase } from 'utils'

import { isPullThroughRatioVisibleOnScreen } from './pullThroughRatioScreens'

type Props = {
  /** FloaterTabs pageUrls key, e.g. loanOverview, budget */
  screenKey?: string | null
  className?: string
}

export const PullThroughRatioPartiesTable = ({ screenKey, className = '' }: Props) => {
  const { hasPermission } = usePermissions()
  const [borrower, loanDetail, globalSettingV2] = useSelector((state: any) => [
    state.borrower,
    state.loanDetail,
    state.globalConfigReducer[GlobalConfigType.GlobalSettingV2],
  ])

  if (!screenKey || !isPullThroughRatioVisibleOnScreen(globalSettingV2, screenKey)) return null
  if (!hasPermission('Loan.Party')) return null
  if (!loanDetail?.loanNumber || loanDetail.loanNumber === 'New') return null

  const partiesMap = partiesMapWithAccountTypes()
  const pullThroughRatioKeys = globalSettingV2?.pullThroughRatioKeys || {}
  const hidePtr = !!globalSettingV2?.hidePullThroughRatio
  const viewKeys: string[] = globalSettingV2?.pullThroughRatioView || []

  return (
    <div className={`score flex-1 ${className}`}>
      <div className="w-fit overflow-hidden rounded-lg border border-slate-300">
        <table className="text-[13.5px]">
          <thead>
            <tr>
              <th className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200" rowSpan={2}>
                Party
              </th>
              <th className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200" rowSpan={2}>
                Name
              </th>
              {!hidePtr && (
                <th
                  className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200 text-center"
                  colSpan={2}
                >
                  Pull Through Ratio
                </th>
              )}
              {hidePtr && (
                <>
                  <th
                    className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200"
                    rowSpan={2}
                  >
                    Email
                  </th>
                  <th
                    className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200"
                    rowSpan={2}
                  >
                    Phone
                  </th>
                </>
              )}
            </tr>
            {!hidePtr && (
              <tr>
                <th className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200">
                  Lifetime
                </th>
                <th className="bg-slate-100 text-slate-600 font-medium px-3 py-1.5 border border-slate-200">
                  Recent Quarter
                </th>
              </tr>
            )}
          </thead>
          <tbody>
            {viewKeys.map((key: string) => {
              if (key === 'lender' && !hidePtr) return null
              if (key === 'borrower') {
                const { firstName, middleName, lastName, entityTitle, email, phone } = (borrower.borrower || {}) as any
                return (
                  <tr key={key}>
                    <td className="border border-slate-200 px-3 py-1.5">Borrower</td>
                    <td className="border border-slate-200 px-3 py-1.5 max-w-[180px] break-words">
                      {pullThroughRatioKeys[key] === 'name'
                        ? getFullName(firstName, middleName, lastName)
                        : entityTitle}
                    </td>
                    {!hidePtr ? (
                      <>
                        <td></td>
                        <td></td>
                      </>
                    ) : (
                      <>
                        <td className="border border-slate-200 px-3 py-1.5">{email}</td>
                        <td className="border border-slate-200 px-3 py-1.5">
                          <PhoneLink value={phone} />
                        </td>
                      </>
                    )}
                  </tr>
                )
              }

              let party = loanDetail.parties?.[key]
              if (key === 'lender' || (!party && loanDetail.closeParties)) {
                const closeParty =
                  loanDetail.closeParties?.[key] ||
                  loanDetail.closeParties?.[toCamelCase(pullThroughRatioPartiesMap[key] || partiesMap[key] || key)] ||
                  {}
                if (key === 'lender' || closeParty.Company || closeParty.EMail || closeParty.FirstName) {
                  party = {
                    companyName: closeParty.Company,
                    name: [closeParty.FirstName, closeParty.MiddleName, closeParty.LastName].filter(Boolean).join(' '),
                    email: closeParty.EMail,
                    phone: phoneConvertor(closeParty.WorkPhone),
                  }
                }
              }

              return (
                <tr key={key}>
                  <td className="border border-slate-200 px-3 py-1.5">
                    {key === 'lender' ? 'Lender' : partiesMap[key] || key}
                  </td>
                  <td className="border border-slate-200 px-3 py-1.5 max-w-[180px] break-words">
                    {party?.[pullThroughRatioKeys[key] || 'companyName']}
                  </td>
                  {!hidePtr && (
                    <>
                      <td className="border border-slate-200 px-3 py-1.5">
                        {party?.score && (
                          <Tooltip
                            message={`<div class="leading-[16px]">Submitted: ${party.score.created}<br/>Funded: ${party.score.funded}<br/>Withdrawn: ${party.score.withdrawn}</div>`}
                          >
                            <span className="flex gap-1 items-center cursor-pointer">
                              {getPrice2decimal(party.score.value, false, true)}{' '}
                              <QuestionMarkCircleIcon className="w-[14px] h-[14px]" />
                            </span>
                          </Tooltip>
                        )}
                      </td>
                      <td className="border border-slate-200 px-3 py-1.5">
                        {party?.quarterScore && (
                          <Tooltip
                            message={`<div class="leading-[16px]">Submitted: ${party.quarterScore.created}<br/>Funded: ${party.quarterScore.funded}<br/>Withdrawn: ${party.quarterScore.withdrawn}</div>`}
                          >
                            <span className="flex gap-1 items-center cursor-pointer">
                              {getPrice2decimal(party.quarterScore.value, false, true)}{' '}
                              <QuestionMarkCircleIcon className="w-[14px] h-[14px]" />
                            </span>
                          </Tooltip>
                        )}
                      </td>
                    </>
                  )}
                  {hidePtr && (
                    <>
                      <td className="border border-slate-200 px-3 py-1.5">{party?.email}</td>
                      <td className="border border-slate-200 px-3 py-1.5">
                        <PhoneLink value={party?.phone} />
                      </td>
                    </>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
